import { ScrollingModule } from '@angular/cdk/scrolling';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  effect,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
  untracked,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Router,
  TitleStrategy,
  UrlSerializer,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { AG_GRID_TRANSLATIONS, CDS_DATETIME_HOUR12, CDS_ICON_PATH } from '@copado/cds';
import {
  currentRegion,
  Logger,
  LoggingErrorHandler,
  LogLevel,
  Region,
  SupportedLocales,
} from '@crt/sui';
import {
  NgbActiveModal,
  NgbCarouselConfig,
  NgbModalConfig,
  NgbOffcanvasConfig,
  NgbPopoverConfig,
  NgbToastModule,
  NgbTooltipConfig,
} from '@ng-bootstrap/ng-bootstrap';
import { LicenseManager } from 'ag-grid-enterprise';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';

import { routes } from '@app/app-routing';
import { AppConstants } from '@app/app.constants.service';
import CustomUrlSerializer from '@app/custom-url-serializer';
import { translations } from '@app/shared/aggrid-wrapper/translations';
import { AppRouter } from '@app/shared/app-router.service';
import { CRTTitleStrategy } from '@app/shared/crt-title-strategy';
import { EnvironmentService } from '@app/shared/environment.service';
import { VersionService } from '@app/shared/version.service';
import { xsrfInterceptor } from '@app/xsrf.interceptor';

LicenseManager.setLicenseKey(AppConstants.AG_GRID_LICENSE);

export function NgbCarouselConfigFactory(): NgbCarouselConfig {
  const config = new NgbCarouselConfig();
  config.interval = 5000;
  config.pauseOnHover = true;
  config.showNavigationArrows = false;
  config.showNavigationIndicators = false;
  return config;
}

export function NgbTooltipConfigFactory(): NgbTooltipConfig {
  const config = new NgbTooltipConfig();
  config.animation = true;
  config.container = 'body';
  config.triggers = 'hover';
  return config;
}

export function NgbModalConfigFactory(): NgbModalConfig {
  const config = new NgbModalConfig();
  /**
   * Disable dismissing the modal by clicking the backdrop.
   * This forces the user to either click the 'X' or Cancel buttons.
   */
  config.backdrop = 'static';
  /** Position it to the center of the screen. */
  config.centered = true;
  /** Prevent dismissing by clicking Escape key. */
  config.keyboard = false;
  /** Display the modal as large. */
  config.size = 'lg';
  return config;
}

export function NgbOffcanvasConfigFactory(): NgbOffcanvasConfig {
  const config = new NgbOffcanvasConfig();
  /** Custom CSS classes for the offcanvas. `w-50` for 50% of the screen width. */
  config.panelClass = 'w-50';
  /** Position it to the right. */
  config.position = 'end';
  return config;
}

export function NgbPopoverConfigFactory(): NgbPopoverConfig {
  const config = new NgbPopoverConfig();
  config.animation = true;
  config.container = 'body';
  return config;
}

export const appConfig: (appLang: SupportedLocales) => ApplicationConfig = (appLang) => ({
  providers: [
    {
      provide: Logger,
      useFactory: (): Logger => {
        const env = inject(EnvironmentService);
        const versionService = inject(VersionService);

        const logger = new Logger();

        effect(() => {
          const version = versionService.currentVersion();
          untracked(() => {
            if (version) {
              logger.configure({
                coralogix: env.isCoralogixEnabled
                  ? {
                      application: 'Copado-CRT',
                      coralogixDomain: 'US2',
                      environment: env.region,
                      ignoreUrls: [/\/local$/, /\/jwt/, /\/user\/signup/],
                      instrumentations: {
                        custom: true,
                        errors: false,
                        fetch: true,
                        interactions: false,
                        long_tasks: true,
                        resources: true,
                        web_vitals: true,
                        xhr: true,
                      },
                      public_key: 'cxtp_Oq75GhcR8QrFNprcVLFDwPrxC7JgkN',
                      sessionRecordingConfig: {
                        enable: false,
                        autoStartSessionRecording: false,
                        sessionRecordingSampleRate: 0,
                      },
                      version: `crt-${versionService.currentVersion()}`,
                    }
                  : undefined,
                enableConsole: true,
                minLevel: env.isLocal ? LogLevel.Debug : LogLevel.Warning,
                sessionId: env.sessionId,
                version,
              });
            }
          });
        });

        return logger;
      },
    },
    { provide: ErrorHandler, useClass: LoggingErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideServiceWorker('kill-sw.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    importProvidersFrom(
      BrowserModule,
      ScrollingModule,
      FormsModule,
      NgbToastModule,
      ReactiveFormsModule,
    ),
    provideAppInitializer(() => inject(EnvironmentService).loadFirebaseConfig()),
    provideFirebaseApp(() => initializeApp(inject(EnvironmentService).getFirebaseConfig())),
    provideAuth(() => getAuth()),
    provideHttpClient(withInterceptors([xsrfInterceptor]), withFetch()),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withComponentInputBinding(),
    ),
    CookieService,
    NgbActiveModal,
    {
      provide: APP_BASE_HREF,
      useFactory: (document: Document): string => {
        const base = document.querySelector('base');
        return base?.getAttribute('href') ?? '/';
      },
      deps: [DOCUMENT],
    },
    { provide: AG_GRID_TRANSLATIONS, useFactory: translations },
    { provide: LOCALE_ID, useValue: appLang },
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
    { provide: CDS_ICON_PATH, useValue: '/assets/icons.svg' },
    {
      provide: CDS_DATETIME_HOUR12,
      useValue: !(currentRegion === Region.Local || currentRegion === Region.EU),
    },
    { provide: NgbCarouselConfig, useFactory: (): NgbCarouselConfig => NgbCarouselConfigFactory() },
    {
      provide: NgbOffcanvasConfig,
      useFactory: (): NgbOffcanvasConfig => NgbOffcanvasConfigFactory(),
    },
    { provide: NgbModalConfig, useFactory: (): NgbModalConfig => NgbModalConfigFactory() },
    { provide: NgbPopoverConfig, useFactory: (): NgbPopoverConfig => NgbPopoverConfigFactory() },
    { provide: NgbTooltipConfig, useFactory: (): NgbTooltipConfig => NgbTooltipConfigFactory() },
    { provide: TitleStrategy, useClass: CRTTitleStrategy },
    { provide: Router, useClass: AppRouter },
    provideAnimations(),
  ],
});
